import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { MobileMenuProvider } from './context/MobileMenuContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import MobileStickyBar from './components/MobileStickyBar';

// Pages
import Home from './pages/Home';
import Capabilities from './pages/Capabilities';
import Platform from './pages/Platform';
import AIAgents from './pages/AIAgents';
import Industries from './pages/Industries';
import EnterpriseAI from './pages/EnterpriseAI';
import AIGovernance from './pages/AIGovernance';
import Clientele from './pages/Clientele';
import About from './pages/About';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Applications from './pages/Applications';
import KnowledgeHub from './pages/KnowledgeHub';
import AIInvestigation from './pages/AIInvestigation';
import AIDataBridge from './pages/AIDataBridge';
import AINewsHub from './pages/AINewsHub';
import AINewsArticle from './pages/AINewsArticle';

// Component to validate language parameter
function LanguageValidator({ children }) {
  const { lang } = useParams();
  
  // If language is invalid, redirect to English
  if (!['en', 'bm', 'zh'].includes(lang)) {
    return <Navigate to="/en" replace />;
  }
  
  return children;
}

function RedirectLegacyAiInvestigationToE1Analytics() {
  const { lang } = useParams();
  if (!['en', 'bm', 'zh'].includes(lang)) {
    return <Navigate to="/en/e1-analytics" replace />;
  }
  return <Navigate to={`/${lang}/e1-analytics`} replace />;
}

function ConditionalFooter() {
  return <Footer />;
}

// Layout component to handle conditional footer rendering
function AppLayout() {
  return (
    <LanguageProvider>
      <MobileMenuProvider>
        <div className="app">
          <Header />
          <main>
            <Routes>
              <Route path="/:lang" element={<LanguageValidator><Home /></LanguageValidator>} />
              <Route path="/:lang/capabilities" element={<LanguageValidator><Capabilities /></LanguageValidator>} />
              <Route path="/:lang/platform" element={<LanguageValidator><Platform /></LanguageValidator>} />
              <Route path="/:lang/applications" element={<LanguageValidator><Applications /></LanguageValidator>} />
              <Route path="/:lang/ai-agents" element={<LanguageValidator><AIAgents /></LanguageValidator>} />
              <Route path="/:lang/industries" element={<LanguageValidator><Industries /></LanguageValidator>} />
              <Route path="/:lang/enterprise-ai" element={<LanguageValidator><EnterpriseAI /></LanguageValidator>} />
              <Route path="/:lang/ai-governance" element={<LanguageValidator><AIGovernance /></LanguageValidator>} />
              <Route path="/:lang/clientele" element={<LanguageValidator><Clientele /></LanguageValidator>} />
              <Route path="/:lang/about" element={<LanguageValidator><About /></LanguageValidator>} />
              <Route path="/:lang/contact" element={<LanguageValidator><Contact /></LanguageValidator>} />
              <Route path="/:lang/privacy-policy" element={<LanguageValidator><PrivacyPolicy /></LanguageValidator>} />
              <Route path="/:lang/terms-of-use" element={<LanguageValidator><TermsOfUse /></LanguageValidator>} />
              <Route path="/:lang/knowledge-hub" element={<LanguageValidator><KnowledgeHub /></LanguageValidator>} />
              <Route path="/:lang/e1-analytics" element={<LanguageValidator><AIInvestigation /></LanguageValidator>} />
              <Route path="/:lang/ai-investigation" element={<RedirectLegacyAiInvestigationToE1Analytics />} />
              <Route path="/:lang/e1-databridge" element={<LanguageValidator><AIDataBridge /></LanguageValidator>} />
              <Route path="/:lang/ai-news" element={<LanguageValidator><AINewsHub /></LanguageValidator>} />
              <Route path="/:lang/ai-news/:slug" element={<LanguageValidator><AINewsArticle /></LanguageValidator>} />
              {/* Redirect root to /en */}
              <Route path="/" element={<Navigate to="/en" replace />} />
              {/* Catch all - redirect to /en */}
              <Route path="*" element={<Navigate to="/en" replace />} />
            </Routes>
          </main>
          <ConditionalFooter />
          <MobileStickyBar />
        </div>
      </MobileMenuProvider>
    </LanguageProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <ScrollToTop />
        <AppLayout />
      </Router>
    </ThemeProvider>
  );
}

export default App;
